import { useCallback, useEffect, useRef, useState } from 'react'
import {
  clampPan,
  clampZoom,
  fitContain,
  isTooCramped,
  toNormalized,
  tokenPixels,
  type Fit,
  type ImageSize,
} from './geometry'
import type { LiveToken, Ping } from './types'
import styles from './live.module.css'

export interface BattleMapProps {
  mapImageUrl: string | null
  tokens: readonly LiveToken[]
  /**
   * Whether this viewer may move anything. **False binds no drag handlers at all** rather than
   * binding disabled ones: a control a player can see and press is a promise the feature does not
   * make (`docs/web-api/phase-9-kickoff.md` §1, decision 3).
   */
  canControl: boolean
  /** Which token is selected, for the keyboard path. Null when none is. */
  selectedId: string | null
  onSelect: (tokenId: string | null) => void
  /** Called once, on `pointerup`, with the committed position. Never mid-gesture. */
  onMove: (tokenId: string, x: number, y: number) => void
  /** The GM tapping the map to say *there*. Absent for a player, who has no such gesture. */
  onPing?: ((x: number, y: number) => void) | undefined
  ping: Ping | null
}

/**
 * The map, and everything standing on it.
 *
 * **DOM under one CSS transform, not a canvas.** A canvas with a fixed internal size would be a
 * device-independent coordinate space — but so is `0..1`, and it would then charge for
 * accessibility (a screen reader sees one opaque element), for testability (jsdom does not render
 * canvas), for device-pixel-ratio handling, and for token portraits. The one property worth
 * reaching for — map and tokens sharing a single transform so they cannot drift apart — is a
 * `transform` on the container they both live in (§3.6).
 *
 * The map's aspect ratio is **taken from the image**, never assumed. Generated maps are 3:2, but
 * a Game Master can upload one of any shape, and normalized coordinates only stay true if the box
 * they are measured against is the picture itself.
 *
 * Pan and zoom are **local to this device**. The GM zooming into a corner moves nobody: a shared
 * camera is motion sickness on a phone and takes the map away from a player mid-look. The ping is
 * what buys that back.
 */
export function BattleMap({
  mapImageUrl,
  tokens,
  canControl,
  selectedId,
  onSelect,
  onMove,
  onPing,
  ping,
}: BattleMapProps) {
  const frame = useRef<HTMLDivElement | null>(null)

  const [viewport, setViewport] = useState({ width: 0, height: 0 })
  const [image, setImage] = useState<ImageSize | null>(null)
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })

  /** The token being dragged and where it currently sits, in normalized coordinates. */
  const [dragging, setDragging] = useState<{ tokenId: string; x: number; y: number } | null>(null)

  /**
   * A pan in progress: where the pointer went down, and what the pan was at that moment.
   *
   * Held rather than accumulated per move, so the map tracks the pointer exactly instead of
   * drifting by a pixel per event.
   */
  const [panning, setPanning] = useState<{
    pointerId: number
    startX: number
    startY: number
    originX: number
    originY: number
  } | null>(null)

  // The pixel mapping is a property of the rendered box, so it is re-derived when the box
  // changes — a rotate, a keyboard opening, a window resize. The state never learns about pixels.
  useEffect(() => {
    const element = frame.current
    if (element === null) return

    const observer = new ResizeObserver(([entry]) => {
      if (entry === undefined) return
      const box = entry.contentRect
      setViewport({ width: box.width, height: box.height })
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const fit: Fit =
    image === null ? { width: 0, height: 0, left: 0, top: 0 } : fitContain(viewport, image)

  const pointToNormalized = useCallback(
    (clientX: number, clientY: number) => {
      const element = frame.current
      if (element === null) return { x: 0, y: 0 }

      const box = element.getBoundingClientRect()

      // Undo the transform before measuring: the pointer is in screen space and the fit is in
      // untransformed layout space.
      const localX = (clientX - box.left - pan.x) / zoom
      const localY = (clientY - box.top - pan.y) / zoom

      return toNormalized(fit, localX, localY)
    },
    [fit, pan.x, pan.y, zoom],
  )

  const cramped = isTooCramped(viewport)

  if (mapImageUrl === null) {
    return (
      <div className={styles.mapEmpty}>
        <p>
          This session has no map. It may have been deleted after the session started — the fight is
          still real, but there is nothing to draw it on.
        </p>
      </div>
    )
  }

  return (
    <div className={styles.mapOuter}>
      {/* The one screen in the app that asks for landscape. A 3:2 map fitted to a 390px-wide
          portrait phone is a 260px strip with unreadable zone numbers; rotating is 2.25x the
          area, for free (§3.6). A prompt rather than a squeezed layout, because the squeezed
          layout is the thing that does not work. */}
      {cramped && (
        <p className={styles.rotate} role="status">
          Turn your phone sideways — the map is drawn wide, and there is more than twice as much of
          it in landscape.
        </p>
      )}

      {/* A labelled region rather than a bare div: the map is a meaningful area of the page,
          and a token's name now also appears in the turn strip and the legend — so "the
          button called Harry" is ambiguous without saying which of the three is meant. */}
      <div
        className={`${styles.mapFrame} ${zoom > 1 ? styles.mapFramePannable : ''}`}
        ref={frame}
        role="group"
        aria-label="Battle map"
        onDoubleClick={() => {
          setZoom(1)
          setPan({ x: 0, y: 0 })
        }}
        onWheel={(event) => {
          if (!event.ctrlKey && !event.metaKey) return
          event.preventDefault()

          setZoom((current) => {
            const next = clampZoom(current - event.deltaY * 0.002)
            // Re-clamp on the way out: zooming back in shrinks the overflow, and a pan that was
            // legal at 4x would otherwise leave the map hanging off the frame at 2x.
            setPan((currentPan) => clampPan(currentPan, viewport, next))
            return next
          })
        }}
        /* Dragging the map itself pans it — but only when there is something to pan to. At
           zoom 1 the map already fits, so the gesture is inert rather than jittery. Tokens
           stop this from reaching here by capturing the pointer themselves. */
        onPointerDown={(event) => {
          if (zoom <= 1) return
          if (event.target !== event.currentTarget && !(event.target as HTMLElement).closest('img'))
            return

          event.currentTarget.setPointerCapture(event.pointerId)
          setPanning({
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            originX: pan.x,
            originY: pan.y,
          })
        }}
        onPointerMove={(event) => {
          if (panning === null || panning.pointerId !== event.pointerId) return

          setPan(
            clampPan(
              {
                x: panning.originX + (event.clientX - panning.startX),
                y: panning.originY + (event.clientY - panning.startY),
              },
              viewport,
              zoom,
            ),
          )
        }}
        onPointerUp={(event) => {
          if (panning === null || panning.pointerId !== event.pointerId) return
          event.currentTarget.releasePointerCapture(event.pointerId)
          setPanning(null)
        }}
        onPointerCancel={() => setPanning(null)}
      >
        {/* One transform, one container, map and tokens inside it. This is the property a canvas
            would have bought and a CSS transform buys for nothing: they cannot drift apart. */}
        <div
          className={styles.mapStage}
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
        >
          <img
            className={styles.mapImage}
            src={mapImageUrl}
            alt="The battle map"
            draggable={false}
            onLoad={(event) =>
              setImage({
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              })
            }
          />

          {image !== null &&
            tokens.map((token) => {
              const live =
                dragging !== null && dragging.tokenId === token.tokenId ? dragging : token

              return (
                <Token
                  key={token.tokenId}
                  token={token}
                  x={live.x}
                  y={live.y}
                  fit={fit}
                  selected={selectedId === token.tokenId}
                  canControl={canControl}
                  onSelect={() => onSelect(token.tokenId)}
                  onDragTo={(x, y) => setDragging({ tokenId: token.tokenId, x, y })}
                  onDrop={(x, y) => {
                    setDragging(null)
                    onMove(token.tokenId, x, y)
                  }}
                  pointToNormalized={pointToNormalized}
                />
              )
            })}

          {ping !== null && image !== null && (
            <span
              className={styles.ping}
              aria-hidden="true"
              style={{
                left: fit.left + ping.x * fit.width,
                top: fit.top + ping.y * fit.height,
              }}
            />
          )}
        </div>
      </div>

      <div className={styles.mapTools}>
        <button
          type="button"
          className={styles.mapTool}
          onClick={() =>
            setZoom((current) => {
              const next = clampZoom(current - 0.5)
              setPan((currentPan) => clampPan(currentPan, viewport, next))
              return next
            })
          }
          aria-label="Zoom out"
        >
          −
        </button>
        <span className={styles.zoomLabel} aria-live="polite">
          {Math.round(zoom * 100)}%
        </span>
        <button
          type="button"
          className={styles.mapTool}
          onClick={() => setZoom((current) => clampZoom(current + 0.5))}
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          type="button"
          className={styles.mapTool}
          onClick={() => {
            setZoom(1)
            setPan({ x: 0, y: 0 })
          }}
        >
          Fit
        </button>

        {canControl && onPing !== undefined && (
          <button
            type="button"
            className={styles.mapTool}
            onClick={() => onPing(0.5, 0.5)}
            title="Flash a marker on everyone's screen"
          >
            Point here
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * One token.
 *
 * A `<button>` rather than a `<div>`, so it is focusable, announced, and operable from a keyboard
 * without any of that being rebuilt. That is most of the argument against a canvas in one element.
 */
function Token({
  token,
  x,
  y,
  fit,
  selected,
  canControl,
  onSelect,
  onDragTo,
  onDrop,
  pointToNormalized,
}: {
  token: LiveToken
  x: number
  y: number
  fit: Fit
  selected: boolean
  canControl: boolean
  onSelect: () => void
  onDragTo: (x: number, y: number) => void
  onDrop: (x: number, y: number) => void
  pointToNormalized: (clientX: number, clientY: number) => { x: number; y: number }
}) {
  /**
   * Sized in the stage's own untransformed space, so the container's `scale(zoom)` grows a token
   * along with the map under it.
   *
   * It used to be divided by the zoom, to hold a constant on-screen size — the reasoning being
   * that zooming is for reading the map rather than for enlarging the pieces. That was wrong in
   * practice: a token pinned at 44px while the room beneath it triples stops covering the ground
   * it is meant to occupy, so a figure standing in a doorway at 1x is standing in a corridor at
   * 3x. A token marks a position *on the map*, so it scales with the map.
   *
   * The floor still does its job, because it is applied before the transform: at 1x — the least
   * zoomed the map ever is — a token is never smaller than the 44px floor.
   */
  const scaled = tokenPixels(fit, token.size)

  const dragHandlers = canControl
    ? {
        onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => {
          event.preventDefault()
          event.currentTarget.setPointerCapture(event.pointerId)
          onSelect()
        },
        onPointerMove: (event: React.PointerEvent<HTMLButtonElement>) => {
          if (!event.currentTarget.hasPointerCapture(event.pointerId)) return
          const next = pointToNormalized(event.clientX, event.clientY)
          onDragTo(next.x, next.y)
        },
        onPointerUp: (event: React.PointerEvent<HTMLButtonElement>) => {
          if (!event.currentTarget.hasPointerCapture(event.pointerId)) return
          event.currentTarget.releasePointerCapture(event.pointerId)

          // The one message a move sends, at the one moment it sends it (§1, decision 7).
          const next = pointToNormalized(event.clientX, event.clientY)
          onDrop(next.x, next.y)
        },
      }
    : {}

  return (
    <button
      type="button"
      className={[
        styles.token,
        selected ? styles.tokenSelected : '',
        token.hidden ? styles.tokenHidden : '',
        token.isDead ? styles.tokenDead : '',
      ].join(' ')}
      style={{
        left: fit.left + x * fit.width,
        top: fit.top + y * fit.height,
        width: scaled,
        height: scaled,
      }}
      aria-pressed={selected}
      aria-label={`${token.label}${token.side === null ? '' : `, ${token.side}`}${token.isDead ? ', dead' : ''}${token.hidden ? ', hidden from players' : ''}`}
      onClick={onSelect}
      {...dragHandlers}
    >
      {token.portraitUrl === null ? (
        <span className={styles.tokenInitial} aria-hidden="true">
          {token.label.slice(0, 1).toUpperCase()}
        </span>
      ) : (
        <img className={styles.tokenPortrait} src={token.portraitUrl} alt="" draggable={false} />
      )}

      {/* The number, over most of the token.
          Six zombies share one portrait, so at 44px across a table the picture says which horde
          and the numeral is the only thing that says *which zombie* — which is what the Game
          Master is naming out loud. Sized from the token's own pixels so it grows with the map's
          zoom and with the token's `size`; `aria-hidden` because the button's label already reads
          "Zombie 2". */}
      {token.ordinal !== null && (
        <span className={styles.tokenOrdinal} style={{ fontSize: scaled * 0.6 }} aria-hidden="true">
          {token.ordinal}
        </span>
      )}

      {/* A cross drawn over the portrait, not a colour change: at 44px a tint reads as "selected"
          or "dimmed" and this has to read as "dead" across a table. The name in the accessible
          label already says it, so this is aria-hidden. */}
      {token.isDead && (
        <svg className={styles.tokenCross} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path
            d="M4 4l16 16M20 4L4 20"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </svg>
      )}
    </button>
  )
}
